import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';

import Template from './template';
import { UserRegister, SelectBox } from 'components';
import { useApiRequest } from 'hooks';
import { apiGetUserProfile, apiPartnerUserModify } from 'api/ZimssaAPI';
import { PartnerAuthState } from 'stores/PartnerAuthStore';
import { USER_LIST } from 'router/PageUrl';

import ZimssaConstant, { GUIDE_TYPE } from 'commons/ZimssaConstant';
import { birthdayToDate, getYear, getMonth, getDays, validateInput, validatePhone, validationBirthDay, zeroPad, isSameServiceType, getUrlServiceType } from 'commons/ZimssaHelper';
import {
    PAGE_SERVICE_FAMILYMOVE,
    LABEL_NAME,
    LABEL_PHONE,
    BUTTON_SAVE,
    TITLE_USER,
    BUTTON_REVIEW,
    SELECT_DEFAULT_YEAR,
    SELECT_DEFAULT_MONTH,
    SELECT_DEFAULT_DAY,
} from 'commons/ZimssaString';

function UserInfoView(): React.ReactElement {
    const history = useHistory();
    const { serviceType, partnerUserIdx } = useParams<{ serviceType: string, partnerUserIdx: string }>();
    const [userInfo, setUserInfo] = useState<IUserInfoState>(defaultUserInfoState);
    const [fetchResult, fetchProfile] = useApiRequest<any>(apiGetUserProfile);
    const { partnerIdx, service, isOwner } = useContext(PartnerAuthState);
    const currentService = service.find((item) => isSameServiceType(item.serviceType, serviceType)) || {
        serviceIdx: 0,
        serviceType: '',
        serviceDocStatus: '',
        serviceActiveStatus: '',
    };

    const formDisable: boolean = !userInfo.isReview;

    // 유저 프로필 조회
    useEffect(() => {
        if (currentService.serviceIdx && partnerUserIdx) {
            fetchProfile({
                type: 'REQUEST',
                body: [partnerUserIdx, serviceType, currentService.serviceIdx],
            });
        }
    }, [fetchProfile, partnerUserIdx, currentService.serviceIdx, serviceType]);

    // 조회데이터 스테이트에 세팅
    useEffect(() => {
        const { result, data } = fetchResult;
        if (result === 'success' && data) {
            const responseData: {
                partnerUserIdx: number;
                partnerUserDocStatus: string;
                partnerUserActiveStatus: string;
                profileImage: string;
                name: string;
                phone: string;
                birthday: string;
                isSelf: boolean;
                isOwner: boolean;
            } = data;
            const birthday = moment(birthdayToDate(responseData.birthday));

            setUserInfo({
                partnerUserIdx: responseData.partnerUserIdx,
                isReview: responseData.partnerUserActiveStatus === 'sign_up' && responseData.partnerUserDocStatus === 'approve_reject',
                profileImage: responseData.profileImage,
                name: responseData.name,
                phone: responseData.phone,
                year: birthday.year(),
                month: birthday.month() + 1,
                day: birthday.date(),
                isSelf: responseData.isSelf,
                isOwner: responseData.isOwner,
            });
        }
    }, [fetchResult, currentService.serviceIdx, serviceType]);

    /**
     * 멤버 재등록
     */
    const onClickUserInfoModifyCallback = useCallback(
        (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            if (serviceType && currentService.serviceIdx && userInfo.isReview) {
                apiPartnerUserModify({
                    serviceIdx: currentService.serviceIdx,
                    serviceType,
                    partnerUserIdx: userInfo.partnerUserIdx,
                    profileImage: userInfo.profileImage,
                    name: userInfo.name,
                    phone: userInfo.phone,
                    birthday: moment(new Date(`${userInfo.year}-${zeroPad(userInfo.month)}-${zeroPad(userInfo.day)}`)).format('YYMMDD'),
                }).then((response) => {
                    const { result, message } = response;
                    if (result === 'success') {
                        alert('직원 정보를 재등록 하였습니다.');
                        history.push(`${USER_LIST.url}/${getUrlServiceType(serviceType)}/${currentService.serviceIdx}`);
                    } else {
                        if (message) alert(message);
                    }
                });
            }
        },
        [
            history,
            currentService.serviceIdx,
            serviceType,
            userInfo.day,
            userInfo.isReview,
            userInfo.month,
            userInfo.name,
            userInfo.partnerUserIdx,
            userInfo.phone,
            userInfo.profileImage,
            userInfo.year,
        ]
    );

    const userRegisterProps = {
        title: TITLE_USER,
        profileImage: {
            value: userInfo.profileImage,
            isUpload: userInfo.isReview || !userInfo.profileImage,
            type: GUIDE_TYPE.PROFILE,
            inputName: 'profileImage',
            imgDir: ZimssaConstant.IMG_DIR.PARTNER_USER.PROFILE.replace('{{partnerIdx}}', `${partnerIdx}`),
            onChangeHandler: (type: any, value: string) => {
                setUserInfo({
                    ...userInfo,
                    profileImage: value,
                });
            },
        },
        name: {
            inputProps: {
                inputName: 'name',
                defaultValue: userInfo.name,
                disabled: formDisable,
                maxLength: 20,
                onChange: useCallback(
                    (e: React.ChangeEvent<HTMLInputElement>) => {
                        const { value } = e.target;
                        setUserInfo({
                            ...userInfo,
                            name: value,
                        });
                    },
                    [userInfo]
                ),
            },
            label: LABEL_NAME,
        },
        phone: {
            inputProps: {
                inputName: 'phone',
                value: userInfo.phone,
                disabled: formDisable,
            },
            onChangeHandler: useCallback(
                (type: any, value: string) => {
                    setUserInfo({
                        ...userInfo,
                        phone: value,
                    });
                },
                [userInfo]
            ),
            label: LABEL_PHONE,
        },
        birthDay: {
            children: [
                <SelectBox
                    key={1}
                    {...{
                        inputName: 'year',
                        value: userInfo.year ? userInfo.year : 0,
                        children: [{ name: SELECT_DEFAULT_YEAR, value: 0 }, ...getYear(100)],
                        disabled: formDisable,
                        onChangeHandler: useCallback(
                            (type: any, value: string) => {
                                setUserInfo({
                                    ...userInfo,
                                    year: Number(value),
                                });
                            },
                            [userInfo]
                        ),
                    }}
                />,
                <SelectBox
                    key={2}
                    {...{
                        inputName: 'month',
                        value: userInfo.month ? userInfo.month : 0,
                        children: [{ name: SELECT_DEFAULT_MONTH, value: 0 }, ...getMonth()],
                        disabled: formDisable,
                        onChangeHandler: useCallback(
                            (type: any, value: string) => {
                                setUserInfo({
                                    ...userInfo,
                                    month: Number(value),
                                });
                            },
                            [userInfo]
                        ),
                    }}
                />,
                <SelectBox
                    key={3}
                    {...{
                        inputName: 'day',
                        value: userInfo.day ? userInfo.day : 0,
                        children: [{ name: SELECT_DEFAULT_DAY, value: 0 }, ...getDays(userInfo.year, userInfo.month)],
                        disabled: formDisable,
                        onChangeHandler: useCallback(
                            (type: any, value: string) => {
                                setUserInfo({
                                    ...userInfo,
                                    day: Number(value),
                                });
                            },
                            [userInfo]
                        ),
                    }}
                />,
            ],
        },

        onClickCancel: useCallback(
            (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                history.goBack();
            },
            [history]
        ),
        btnNext: {
            contents: userInfo.isReview ? BUTTON_REVIEW : BUTTON_SAVE,
            isActive: (() => {
                if (isOwner && userInfo.isReview) {
                    return validateInput(userInfo.name.trim()) && validatePhone(userInfo.phone.trim()) && validationBirthDay(`${userInfo.year}${zeroPad(userInfo.month)}${zeroPad(userInfo.day)}`);
                } else {
                    return false;
                }
            })(),
            onClick: userInfo.isReview ? onClickUserInfoModifyCallback : () => {},
        },
    };
    return (
        <Template title={PAGE_SERVICE_FAMILYMOVE}>
            <UserRegister {...userRegisterProps} />
        </Template>
    );
}

export default UserInfoView;

const defaultUserInfoState: IUserInfoState = {
    partnerUserIdx: 0,
    isReview: false,
    profileImage: '',
    name: '',
    phone: '',
    year: 0,
    month: 0,
    day: 0,
    isSelf: false,
    isOwner: false,
};

interface IUserInfoState {
    partnerUserIdx: number;
    isReview: boolean;
    profileImage: string;
    name: string;
    phone: string;
    year: number;
    month: number;
    day: number;
    isSelf: boolean;
    isOwner: boolean;
}
